import { useState, useCallback, useEffect, useRef } from 'react';
import { Shield, CheckCircle, XCircle, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface SecurityHeader {
  name: string;
  value: string | null;
  present: boolean;
  rating: 'good' | 'warning' | 'bad' | 'info';
  description: string;
  recommendation?: string;
}

interface SecurityHeadersResult {
  url: string;
  headers: SecurityHeader[];
  score: number;
  grade: string;
  rawHeaders: Record<string, string>;
}

// In production, nginx serves both frontend and API from same origin
const API_BASE = '';

export function SecurityHeaders({ initialValue }: ToolComponentProps) {
  const [input, setInput] = useState(initialValue || '');
  const [result, setResult] = useState<SecurityHeadersResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRawHeaders, setShowRawHeaders] = useState(false);
  const hasAutoTriggered = useRef(false);

  const handleScan = useCallback(async () => {
    let host = input.trim();
    if (!host) {
      setError('Please enter a hostname');
      return;
    }

    // Strip protocol if provided
    host = host.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/headers/${encodeURIComponent(host)}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Security headers scan failed');
        return;
      }

      setResult(data);

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', host);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [input]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan();
    }
  };

  // Auto-scan if initialValue was provided via URL
  useEffect(() => {
    if (initialValue && !result && !isLoading && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleScan();
    }
  }, [initialValue, result, isLoading, handleScan]);

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'text-[var(--accent)] bg-[var(--accent-dim)]';
      case 'B':
        return 'text-blue-400 bg-blue-500/20';
      case 'C':
        return 'text-orange-500 dark:text-orange-400 bg-orange-500/20';
      case 'D':
        return 'text-orange-400 bg-orange-500/20';
      case 'F':
        return 'text-red-400 bg-red-500/20';
      default:
        return 'text-[var(--text-muted)] bg-[var(--bg-tertiary)]';
    }
  };

  const getRatingIcon = (rating: string) => {
    switch (rating) {
      case 'good':
        return <CheckCircle className="w-5 h-5 text-[var(--accent)]" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-500 dark:text-orange-400" />;
      case 'bad':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-400" />;
      default:
        return null;
    }
  };

  const getRatingBg = (rating: string) => {
    switch (rating) {
      case 'good':
        return 'border-[var(--accent)]/30 bg-[var(--accent)]/5';
      case 'warning':
        return 'border-orange-400/30 bg-orange-400/5';
      case 'bad':
        return 'border-red-400/30 bg-red-400/5';
      case 'info':
        return 'border-blue-400/30 bg-blue-400/5';
      default:
        return 'border-[var(--border-primary)]';
    }
  };

  return (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          Hostname
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="example.com"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleScan}
            disabled={isLoading || !input.trim()}
            className="btn-primary flex items-center justify-center gap-2 px-4 sm:px-6 whitespace-nowrap"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Scanning...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Scan Headers
              </>
            )}
          </button>
        </div>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          Security Headers Analysis
        </label>
        <div className="output-panel">
          {error ? (
            <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
              <span className="font-semibold">Error:</span> {error}
            </div>
          ) : !result ? (
            <div className="text-[var(--text-muted)] text-center py-8">
              Enter a hostname above and click "Scan Headers" to analyze HTTP security headers.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Score & Grade */}
              <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--bg-secondary)]">
                <div className={`text-4xl font-bold px-4 py-2 rounded-lg ${getGradeColor(result.grade)}`}>
                  {result.grade}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-lg text-[var(--text-primary)]">
                      Score: {result.score}/100
                    </span>
                  </div>
                  <div className="text-sm text-[var(--text-muted)] mt-1 flex items-center gap-2">
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent)] hover:underline flex items-center gap-1"
                    >
                      {result.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Security Headers List */}
              <div>
                <h3 className="text-[var(--accent)] font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Security Headers
                </h3>
                <div className="space-y-2">
                  {result.headers.map((header) => (
                    <div
                      key={header.name}
                      className={`p-3 rounded-lg border ${getRatingBg(header.rating)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">{getRatingIcon(header.rating)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-[var(--text-primary)]">{header.name}</span>
                            {header.present ? (
                              <span className="text-xs px-2 py-0.5 rounded bg-[var(--accent)]/20 text-[var(--accent)]">
                                Present
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                                Missing
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[var(--text-muted)]">{header.description}</p>
                          {header.value && (
                            <div className="mt-2 p-2 rounded bg-[var(--bg-primary)] border border-[var(--border-primary)] overflow-x-auto">
                              <code className="text-xs text-[var(--text-secondary)] break-all whitespace-pre-wrap">
                                {header.value}
                              </code>
                            </div>
                          )}
                          {header.recommendation && (
                            <div className="mt-2 flex items-start gap-2 text-sm">
                              <AlertTriangle className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0 mt-0.5" />
                              <span className="text-orange-500 dark:text-orange-400">{header.recommendation}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Raw Headers Toggle */}
              <div>
                <button
                  onClick={() => setShowRawHeaders(!showRawHeaders)}
                  className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors text-sm"
                >
                  <Info className="w-4 h-4" />
                  <span>{showRawHeaders ? 'Hide' : 'Show'} All Response Headers</span>
                </button>
                {showRawHeaders && (
                  <div className="mt-3 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {Object.entries(result.rawHeaders).map(([key, value]) => (
                          <tr key={key} className="border-b border-[var(--border-primary)] last:border-0">
                            <td className="py-2 pr-4 font-mono text-[var(--accent)] whitespace-nowrap text-xs">{key}</td>
                            <td className="py-2 font-mono text-[var(--text-secondary)] break-all text-xs">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
